/**
 * RESUMEN DIGITAL — Main Entry Point (Modular Version)
 */
import { fetchNewsData, detectCountry, getEmbeddedData, SUPPORTED_COUNTRIES } from './modules/api.js';
import { renderAll, renderCritical, renderDeferred, showSkeletons } from './modules/renderers.js';
import { setupInteractions } from './modules/interactions.js';
import { setupScrollAnimations, setupVisibilityTracking } from './modules/animations.js';
import { updateSEO } from './modules/seo.js';
import { setupSecurity, setupAsyncCSS } from './modules/security.js';
import { setupLiveWidgets } from './modules/ui.js';
import { initializeNoticia } from './modules/noticia-renderer.js';
import { setupVideoModal } from './modules/video-modal.js';
import { trackEvent } from './modules/analytics.js';


const startApp = () => {
  'use strict';

  /**
   * Application Orchestrator
   */
  async function init() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const dateParam = urlParams.get('date') || 'today';
      const isHomepage = Boolean(document.getElementById('newsHeroGrid'));
      const embeddedCritical = (isHomepage && dateParam === 'today')
        ? getEmbeddedData('critical-news-data')
        : null;

      if (embeddedCritical) {
        renderCritical(embeddedCritical, dateParam);
      }

      // 1. Core Setups
      setupSecurity();
      setupAsyncCSS();
      setupVideoModal();
      
      const onDateChange = (date) => loadHistoryDate(date);
      setupInteractions(onDateChange);


      // 2. Routing & Geolocation (SPA redirection)
      const routed = await handleRouting();
      if (routed) return;

      // Check if we are on detailing page
      if (document.getElementById('noticia-content')) {
        await initializeNoticia();
      }

      // Show skeletons before fetching homepage
      if (isHomepage && !embeddedCritical) {
        showSkeletons();
      }

      if (embeddedCritical) {
        deferNonCriticalWork(() => loadNews(dateParam, { preserveCritical: true }));
      } else {
        await loadNews(dateParam);
      }

      deferNonCriticalWork(() => {
        setupLiveWidgets();
        setupVisibilityTracking(trackEvent);
      });

    } catch (err) {
      console.error('Initialization error:', err);
      // Fallback animations for static content if data load fails
      setupScrollAnimations();
    } finally {
      // Remove loader for static pages
      const loader = document.getElementById('page-loader');
      if (loader) {
        loader.classList.add('hidden');
        document.body.classList.remove('loading');
      }
    }
  }

  /**
   * Load and render news for a specific date
   */
  async function loadNews(dateParam, options = {}) {
    try {
      const data = await fetchNewsData(dateParam);
      if (!data) throw new Error('No data returned');

      // Render full homepage if we are on index, or render world cup news on the mundial page
      if (document.getElementById('newsHeroGrid') || document.getElementById('wcNewsGrid')) {
        if (options.preserveCritical) {
          renderDeferred(data);
        } else {
          renderAll(data, dateParam);
        }
        if (document.getElementById('newsHeroGrid')) {
          updateSEO(data, dateParam);
        }
      } else {
        // Ticker only for other pages like noticia.html
        const ticker = document.getElementById('breakingTicker');
        if (ticker && data.breaking) {
          ticker.innerHTML = data.breaking.map(item => `<span class="breaking-bar__item">${item}</span>`).join('');
        }
      }

      // Re-trigger animations for newly rendered content
      setupScrollAnimations();
    } catch (err) {
      console.warn('News load failed:', err);
      setupScrollAnimations();
    }
  }

  /**
   * Load historical date (triggered from selector or popstate)
   */
  async function loadHistoryDate(dateStr, fromPopState = false) {
    if (!fromPopState) {
      const newUrl = window.location.pathname + '?date=' + dateStr;
      window.history.pushState({ date: dateStr }, '', newUrl);
    }
    await loadNews(dateStr);
  }

  /**
   * Basic routing and country redirection logic
   */
  async function handleRouting() {
    const path = window.location.pathname;
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || protocol === 'file:';

    if (path === '/' || path === '/index.html' || path === '' || path === '/py' || path === '/py/') {
      if (isLocal) return false;

      try {
        const country = await detectCountry();
        const target = SUPPORTED_COUNTRIES.includes(country) ? country : 'py';
        
        const search = window.location.search;
        // If target is 'py' (default), we want to keep the URL as '/' instead of '/py'
        const targetPath = target === 'py' ? '/' : `/${target}`;
        
        // Only replace if we are NOT already on the target path to avoid loops
        if (path !== targetPath && path !== `${targetPath}/`) {
          window.history.replaceState(null, '', `${targetPath}${search}`);
        }
        trackEvent('page_view', { page_path: `${targetPath}${search}` });
        return false;
      } catch (err) {
        if (path !== '/' && path !== '') {
          window.history.replaceState(null, '', `/${window.location.search}`);
        }
        return false;
      }
    }

    // Verify 2-letter country paths (only for exact matches like /py or /cl)
    const countryMatch = path.match(/^\/([a-z]{2})\/?$/i);
    if (countryMatch && !path.includes('/noticia') && !path.includes('/historial')) {
      const countryCode = countryMatch[1].toLowerCase();
      if (!SUPPORTED_COUNTRIES.includes(countryCode)) {
        console.warn(`Country ${countryCode} not supported`);
        // We avoid hard redirect to 404 to prevent false positives during migration
        return false;
      }
    }
    return false;
  }

  // Note: trackEvent is now imported from analytics.js

  function deferNonCriticalWork(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2500 });
      return;
    }

    window.setTimeout(callback, 1500);
  }

  // Handle browser back/forward navigation
  window.onpopstate = (e) => {
    if (e.state && e.state.date) {
      loadHistoryDate(e.state.date, true);
    } else if (window.location.search.includes('date')) {
      window.location.href = window.location.origin + window.location.pathname;
    }
  };

  // Start the machine
  init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startApp);
} else {
  startApp();
}
